import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { RouteComponentProps } from 'react-router'
import { Column, useColumnOrder, usePagination, useRowSelect, useSortBy, useTable } from 'react-table'
import AddIcon from '@material-ui/icons/Add'
import { Grid } from '@material-ui/core'

import ContainedButton from 'civic-champs-shared/core/ContainedButton'

import { ExportButton, ExtendedPagedTable, useFiltersFromQuery } from 'core/table/components'
import { useGetColumnState, useTableColumnStyles as useStyles } from 'core/table/table-hooks/'
import { MessagesServerSideSelectionProvider } from 'messages/context/MessagesServerSideSelectionContext'
import { useMessagesServerSideSelectionPlugin } from 'messages/hooks/useMessagesServerSideSelectionPlugin'
import { useServerSideMessagesRowSelection } from 'messages/hooks/useServerSideMessagesRowSelection'
import { useServerSideMessagesSelectColumn } from 'messages/hooks/useServerSideMessagesSelectColumn'

import Loading from 'components/Loading'

import { useFetchCustomMessages, useMessagesColumns } from '../hooks'
import { NotificationBlastSummary } from '../types'
import { STRING_OPERATOR_OPTIONS } from 'core/table/interfaces/Filters'
import { useGetAllSearchableContacts } from 'messages/hooks/useGetAllSearchableContacts'
import { CUSTOM_MESSAGES_DATE_RANGE_KEY, MESSAGES_LIST_DEFAULT_DATE_RANGE, SMS_CREDIT_CONTACT } from '../config'
import { useCurrentOrg } from '../../civic-champs-shared/auth/hooks'
import { useDateRangeFilter } from 'filtering/hooks'
import useFetchRefactored from 'civic-champs-shared/api/hooks/useFetchRefactored'
import { useMessageRecipientsPromptExtended } from '../hooks/useMessageRecipientsPrompt'

const csvHeaders = [
  { label: 'Subject', key: 'subject' },
  { label: 'Date', key: 'createdAt' },
  { label: 'Source', key: 'source' },
  { label: 'Source Name', key: 'sourceName' },
  { label: 'Status', key: 'status' },
]

const cards = [
  {
    header: 'Total Messages',
    accessor: 'totalMessages',
  },
  {
    header: 'Emails Sent',
    accessor: 'emailSent',
  },
  {
    header: 'SMS Sent',
    accessor: 'smsSent',
  },
  {
    header: 'SMS Sends Available',
    accessor: 'smsCredits',
    tooltip: `To purchase additional SMS Credits, please contact customer support at ${SMS_CREDIT_CONTACT} `,
  },
]

const tableName = 'customMessages'

const getOperatorOptions = (column: string) => {
  switch (column) {
    case 'subject':
      return STRING_OPERATOR_OPTIONS
    default:
      return []
  }
}

export const CustomMessagesServerSide = ({ location, history }: RouteComponentProps) => {
  const skipReset = React.useRef<boolean>()
  const [fetchColumnState, { loading: columnStateLoading, result: columnState }] = useGetColumnState()
  const columnStyles = useStyles()
  const organization = useCurrentOrg()
  const [dateRange] = useDateRangeFilter({
    key: CUSTOM_MESSAGES_DATE_RANGE_KEY,
    defaultDateRange: MESSAGES_LIST_DEFAULT_DATE_RANGE,
  })

  const {
    messages,
    totalCount,
    pagedSummary,
    loading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    onSearchChange,
    refetchWithCount,
    filterKey,
    fetchAllMatchingMessages,
  } = useFetchCustomMessages(dateRange)

  const [request] = useFetchRefactored<NotificationBlastSummary[]>()
  const [addMessage, onAddMessage] = useMessageRecipientsPromptExtended()
  const getSearchableContacts = useGetAllSearchableContacts()
  const columns = useMessagesColumns()
  useFiltersFromQuery(location.search, getOperatorOptions)

  const pageCount = totalCount === 0 ? 0 : Math.ceil(totalCount / pageSize)
  const tableDispatchRef = useRef<(action: { type: string; selectedRowIds?: Record<string, boolean> }) => void>(
    () => undefined,
  )

  const {
    selectedRowIds,
    selectedCount,
    selectingAll,
    selectionPluginApiRef,
    clearSelection,
    selectAllMatching,
    getSelectedRows,
  } = useServerSideMessagesRowSelection({
    rows: messages,
    getRowId: (row: NotificationBlastSummary) => row.uuid,
    totalCount,
    listLoading: loading,
    fetchAllMatching: fetchAllMatchingMessages,
  })

  const messagesSelectionPlugin = useMessagesServerSideSelectionPlugin(selectionPluginApiRef)

  const selectionContextValue = useMemo(
    () => ({
      totalCount,
      selectedCount,
      selectingAll,
      listLoading: loading,
      onSelectAllMatching: () => selectAllMatching(tableDispatchRef.current),
      onClearSelection: () => clearSelection(tableDispatchRef.current),
    }),
    [clearSelection, loading, selectAllMatching, selectedCount, selectingAll, totalCount],
  )

  const table = useTable(
    {
      data: messages,
      columns: columns as Column<NotificationBlastSummary>[],
      initialState: {
        pageIndex,
        pageSize,
        sortBy: [{ id: 'createdAt', desc: true }],
        selectedRowIds,
      },
      manualPagination: true,
      pageCount,
      autoResetPage: false,
      autoResetSelectedRows: false,
      getRowId: (row: NotificationBlastSummary) => row.uuid,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    messagesSelectionPlugin,
    useServerSideMessagesSelectColumn(() => true, columnStyles.selectColumn),
  )

  tableDispatchRef.current = table.dispatch

  useEffect(() => {
    clearSelection(tableDispatchRef.current)
  }, [filterKey, clearSelection])

  useEffect(() => {
    fetchColumnState(tableName)
  }, [fetchColumnState])

  useEffect(() => {
    if (table.state.pageIndex !== pageIndex) {
      table.gotoPage(pageIndex)
    }
  }, [pageIndex, table])

  useEffect(() => {
    if (table.state.pageSize !== pageSize) {
      table.setPageSize(pageSize)
    }
  }, [pageSize, table])

  useEffect(() => {
    let poll = true
    const unlisten = onAddMessage(async ({ uuid }: { uuid: string }) => {
      while (poll) {
        const page = await request({
          method: 'get',
          url: `/organizations/${organization.id}/notification-blasts`,
          config: { queryStringParameters: { uuid, forGrid: true } },
        })
        if (page?.length) {
          await refetchWithCount()
          poll = false
        } else {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }
    })
    return () => {
      poll = false
      unlisten()
    }
  }, [onAddMessage, organization.id, refetchWithCount, request])

  const summary = useMemo(
    () => ({
      totalMessages: pagedSummary.emailCount + pagedSummary.smsCount,
      emailSent: pagedSummary.emailCount,
      smsSent: pagedSummary.smsCount,
      smsCredits: organization.smsCredits || 0,
    }),
    [organization.smsCredits, pagedSummary],
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPageIndex(newPage)
    },
    [setPageIndex],
  )

  const onNewMessage = () => {
    addMessage({
      recipientPersonIds: [],
      getSearchableContacts,
      autocompleteSectionTitle: 'All Volunteers',
    })
  }

  const selectedRows = useMemo(() => getSelectedRows(), [getSelectedRows, selectedCount])
  const disabled = selectedCount === 0

  if (columnStateLoading || (loading && messages.length === 0)) {
    return <Loading />
  }

  return (
    <MessagesServerSideSelectionProvider value={selectionContextValue}>
      <Grid container>
        <ExtendedPagedTable<NotificationBlastSummary>
          summary={summary}
          cards={cards}
          history={history}
          location={location}
          table={table}
          columns={columns}
          columnState={columnState}
          tableName={tableName}
          searchPlaceholder="Search Messages"
          skipReset={skipReset}
          buttons={
            <>
              <ExportButton
                disabled={disabled}
                data={selectedRows}
                headers={csvHeaders}
                filename={'custom-messages.csv'}
              />
            </>
          }
          addButton={
            <ContainedButton startIcon={<AddIcon />} onClick={onNewMessage}>
              New Message
            </ContainedButton>
          }
          useGlobalSearch
          useDateRange
          customDateKey={CUSTOM_MESSAGES_DATE_RANGE_KEY}
          defaultDateRange={MESSAGES_LIST_DEFAULT_DATE_RANGE}
          serverSide
          totalCount={totalCount}
          pageIndex={pageIndex}
          onPageChange={handlePageChange}
          onPageSizeChange={setPageSize}
          onSearchChange={onSearchChange}
        />
      </Grid>
    </MessagesServerSideSelectionProvider>
  )
}
